import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Stack,
  alpha,
  useTheme,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  TextField,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse
} from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import axios from 'axios';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getPublicApiBase, publicApiUrl } from '../../utils/publicApi';
import { PublicJobDetailBody } from './PublicJobDetailView';
import { usePublicPortalAppearance } from '../../context/PublicPortalAppearanceContext';
import {
  getStoredSplitRatio,
  setStoredSplitRatio,
  setStoredSplitListHidden,
  getStoredTrainingLayoutFormat,
  setStoredTrainingLayoutFormat
} from '../../utils/publicPortalPreferences';

/** Soft unique pastels for training hierarchy levels. */
const TRAINING_HIERARCHY_COLORS = {
  category: { bg: '#DBEAFE', border: '#93C5FD', text: '#1e3a8a', label: 'Category' },
  subcategory: { bg: '#FCE7F3', border: '#F9A8D4', text: '#9d174d', label: 'Sub category' },
  course: { bg: '#DCFCE7', border: '#86EFAC', text: '#166534', label: 'Course' },
  version: { bg: '#FEF3C7', border: '#FCD34D', text: '#92400e', label: 'Version' }
};

function trainingLevelSx(level) {
  const c = TRAINING_HIERARCHY_COLORS[level] || TRAINING_HIERARCHY_COLORS.course;
  return {
    bgcolor: c.bg,
    border: `1.5px solid ${c.border}`,
    color: c.text,
    borderRadius: 1.5
  };
}

/** Total published versions (each edition counts). */
function countPublishedVersions(catOrCourses) {
  if (Array.isArray(catOrCourses)) return catOrCourses.length;
  return (catOrCourses?.publishedCourses || []).length;
}

function versionCountLabel(n) {
  const count = Number(n) || 0;
  return `${count} ${count === 1 ? 'version' : 'versions'}`;
}

function TrainingHierarchyLegend() {
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 0.5 }}>
      {Object.entries(TRAINING_HIERARCHY_COLORS).map(([key, c]) => (
        <Chip
          key={key}
          size="small"
          label={c.label}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: c.bg,
            border: `1px solid ${c.border}`,
            color: c.text
          }}
        />
      ))}
    </Stack>
  );
}

function TrainingLayoutFormatToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(e, v) => {
        if (v) onChange(v);
      }}
      aria-label="Training layout format"
      sx={{
        ml: 'auto',
        flexShrink: 0,
        '& .MuiToggleButton-root': {
          px: 1,
          py: 0.5,
          textTransform: 'none',
          gap: 0.5,
          fontSize: '0.75rem',
          fontWeight: 600
        }
      }}
    >
      <ToggleButton value="list" aria-label="List format">
        <ViewListIcon fontSize="small" />
        List
      </ToggleButton>
      <ToggleButton value="box" aria-label="Box format">
        <ViewModuleIcon fontSize="small" />
        Box
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

function TrainingExpandCollapseBar({ onExpandAll, onCollapseAll }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
      <Button size="small" variant="outlined" startIcon={<UnfoldMoreIcon />} onClick={onExpandAll} sx={{ textTransform: 'none' }}>
        Expand all
      </Button>
      <Button size="small" variant="outlined" startIcon={<UnfoldLessIcon />} onClick={onCollapseAll} sx={{ textTransform: 'none' }}>
        Collapse all
      </Button>
    </Stack>
  );
}

function HierarchySectionHeader({ level, title, subtitle, expanded, onToggle, endAdornment }) {
  const c = TRAINING_HIERARCHY_COLORS[level] || TRAINING_HIERARCHY_COLORS.course;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        px: 1.25,
        py: 0.85,
        cursor: 'pointer',
        userSelect: 'none',
        color: c.text,
        borderBottom: expanded ? `1px solid ${c.border}` : 'none',
        '&:hover': { filter: 'brightness(0.97)' }
      }}
    >
      <Box sx={{ display: 'inline-flex', color: c.text, flexShrink: 0 }}>
        {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, color: c.text, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" sx={{ color: alpha(c.text, 0.8), display: 'block' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {endAdornment}
    </Stack>
  );
}

/**
 * Multi-category tree: Category → Sub category → Course → Version.
 * Category / sub category / course are collapsible (default expanded).
 */
function TrainingHierarchyBrowse({
  categories = [],
  layoutFormat = 'list',
  onOpenVersion
}) {
  const collapseKeys = useMemo(() => {
    const keys = [];
    for (const cat of categories || []) {
      keys.push(`cat:${cat.id}`);
      for (const spot of cat.groups || []) {
        keys.push(`spot:${cat.id}:${spot.id || spot.name}`);
        for (const courseGroup of spot.courses || []) {
          keys.push(`course:${cat.id}:${courseGroup.versionGroupId}`);
        }
      }
    }
    return keys;
  }, [categories]);

  const [expandedMap, setExpandedMap] = useState(() =>
    Object.fromEntries(collapseKeys.map((k) => [k, true]))
  );

  useEffect(() => {
    setExpandedMap((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of collapseKeys) {
        if (!(k in next)) {
          next[k] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [collapseKeys]);

  const isOpen = (key) => expandedMap[key] !== false;
  const toggle = (key) => setExpandedMap((prev) => ({ ...prev, [key]: !isOpen(key) }));
  const expandAll = () => setExpandedMap(Object.fromEntries(collapseKeys.map((k) => [k, true])));
  const collapseAll = () => setExpandedMap(Object.fromEntries(collapseKeys.map((k) => [k, false])));

  if (!categories?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No published training versions to show.
      </Typography>
    );
  }

  const renderVersions = (versions) => {
    if (layoutFormat === 'box') {
      return (
        <Grid container spacing={0.75} sx={{ p: 1 }}>
          {versions.map((c) => (
            <Grid item xs={12} sm={6} key={c.id}>
              <Button
                fullWidth
                size="small"
                onClick={() => onOpenVersion(c.id)}
                sx={{
                  ...trainingLevelSx('version'),
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 1,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#FDE68A', borderColor: '#F59E0B' }
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ color: TRAINING_HIERARCHY_COLORS.version.text }}>
                    {c.edition_label || c.course_code || 'Version'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(TRAINING_HIERARCHY_COLORS.version.text, 0.85) }}>
                    {formatDeliveryType(c.delivery_type)}
                    {c.start_date && c.end_date
                      ? ` · ${c.start_date} → ${c.end_date}`
                      : c.start_date
                        ? ` · ${c.start_date}`
                        : ''}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      );
    }
    return (
      <List dense disablePadding>
        {versions.map((c) => (
          <ListItemButton
            key={c.id}
            onClick={() => onOpenVersion(c.id)}
            alignItems="flex-start"
            sx={{
              ...trainingLevelSx('version'),
              borderRadius: 0,
              borderLeft: 'none',
              borderRight: 'none',
              borderTop: 'none',
              borderBottom: `1px solid ${TRAINING_HIERARCHY_COLORS.version.border}`,
              '&:last-child': { borderBottom: 'none' },
              '&:hover': { bgcolor: '#FDE68A' }
            }}
          >
            <ListItemText
              primary={c.edition_label || c.course_code || 'Version'}
              secondary={`${formatDeliveryType(c.delivery_type)}${
                c.start_date && c.end_date
                  ? ` · ${c.start_date} → ${c.end_date}`
                  : c.start_date
                    ? ` · ${c.start_date}`
                    : ''
              }`}
              primaryTypographyProps={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: TRAINING_HIERARCHY_COLORS.version.text
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: alpha(TRAINING_HIERARCHY_COLORS.version.text, 0.8)
              }}
            />
          </ListItemButton>
        ))}
      </List>
    );
  };

  return (
    <Stack spacing={1.5}>
      {categories.map((cat) => {
        const catKey = `cat:${cat.id}`;
        const catOpen = isOpen(catKey);
        const catVersionCount = (cat.groups || []).reduce(
          (n, spot) => n + (spot.courses || []).reduce((m, g) => m + (g.versions || []).length, 0),
          0
        );
        return (
          <Box key={cat.id} sx={{ ...trainingLevelSx('category'), overflow: 'hidden' }}>
            <HierarchySectionHeader
              level="category"
              title={cat.name}
              subtitle={versionCountLabel(catVersionCount)}
              expanded={catOpen}
              onToggle={() => toggle(catKey)}
            />
            <Collapse in={catOpen} timeout="auto" unmountOnExit={false}>
              <Stack spacing={1} sx={{ p: 1 }}>
                {cat.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                    {cat.description}
                  </Typography>
                ) : null}
                {(cat.groups || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                    No versions in this category.
                  </Typography>
                ) : (
                  (cat.groups || []).map((spot) => {
                    const spotKey = `spot:${cat.id}:${spot.id || spot.name}`;
                    const spotOpen = isOpen(spotKey);
                    const spotVersionCount = (spot.courses || []).reduce((m, g) => m + (g.versions || []).length, 0);
                    return (
                      <Box key={spotKey} sx={{ ...trainingLevelSx('subcategory'), overflow: 'hidden' }}>
                        <HierarchySectionHeader
                          level="subcategory"
                          title={spot.name}
                          subtitle={versionCountLabel(spotVersionCount)}
                          expanded={spotOpen}
                          onToggle={() => toggle(spotKey)}
                        />
                        <Collapse in={spotOpen} timeout="auto" unmountOnExit={false}>
                          <Stack spacing={1} sx={{ p: 1 }}>
                            {(spot.courses || []).map((courseGroup) => {
                              const courseKey = `course:${cat.id}:${courseGroup.versionGroupId}`;
                              const courseOpen = isOpen(courseKey);
                              const vCount = (courseGroup.versions || []).length;
                              return (
                                <Box key={courseKey} sx={{ ...trainingLevelSx('course'), overflow: 'hidden' }}>
                                  <HierarchySectionHeader
                                    level="course"
                                    title={courseGroup.name}
                                    subtitle={versionCountLabel(vCount)}
                                    expanded={courseOpen}
                                    onToggle={() => toggle(courseKey)}
                                  />
                                  <Collapse in={courseOpen} timeout="auto" unmountOnExit={false}>
                                    {renderVersions(courseGroup.versions || [])}
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Collapse>
                      </Box>
                    );
                  })
                )}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
      <TrainingExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
    </Stack>
  );
}

const scrollToHash = (hash) => {
  if (!hash || hash.length < 2) return;
  const id = hash.replace('#', '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function matchRemoteOption(/** @type {string} */ filterVal, /** @type {string|undefined} */ remoteOption) {
  const ro = (remoteOption || '').toLowerCase();
  if (filterVal === 'any') return true;
  if (filterVal === 'remote') return ro.includes('remote');
  if (filterVal === 'hybrid') return ro.includes('hybrid');
  if (filterVal === 'onsite') {
    return ro.includes('on-site') || ro.includes('onsite') || ro.includes('office') || ro.includes('in office');
  }
  return true;
}

/** @param {{ publishedCourses?: Array<{ subCategoryId?: string, subCategoryName?: string, version_group_id?: string, name?: string, edition_number?: number }> }} cat */
function groupCoursesByLearningSpot(publishedCourses) {
  const map = new Map();
  for (const c of publishedCourses || []) {
    const key = c.subCategoryId || '_';
    if (!map.has(key)) {
      map.set(key, {
        id: c.subCategoryId,
        name: c.subCategoryName || 'Sub category',
        courseGroups: []
      });
    }
    map.get(key).courseGroups.push(c);
  }
  // Nest: subcategory → course (version group) → versions
  return [...map.values()].map((spot) => {
    const byVersion = new Map();
    for (const c of spot.courseGroups) {
      const vg = c.version_group_id || c.id;
      if (!byVersion.has(vg)) {
        byVersion.set(vg, {
          versionGroupId: vg,
          name: c.name,
          versions: []
        });
      }
      const group = byVersion.get(vg);
      if (!group.name && c.name) group.name = c.name;
      group.versions.push(c);
    }
    const courses = [...byVersion.values()].map((g) => {
      g.versions.sort(
        (a, b) => (Number(a.edition_number) || 0) - (Number(b.edition_number) || 0) || String(a.name).localeCompare(String(b.name))
      );
      return g;
    });
    courses.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return { id: spot.id, name: spot.name, courses };
  });
}

function formatDeliveryType(/** @type {string|undefined} */ v) {
  if (!v) return '—';
  const s = String(v).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatOutlineFormat(/** @type {string|undefined} */ v) {
  if (!v) return '—';
  const s = String(v).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatCourseSchedule(course) {
  if (!course) return null;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const parts = [];
  if (course.start_date && course.end_date) {
    parts.push(`${course.start_date} → ${course.end_date}`);
  } else if (course.start_date || course.end_date) {
    parts.push(course.start_date || course.end_date);
  }
  if (Array.isArray(course.weekdays) && course.weekdays.length) {
    parts.push(course.weekdays.map((n) => dayNames[n] || n).join(', '));
  } else if (typeof course.weekdays === 'string' && course.weekdays.trim()) {
    parts.push(course.weekdays);
  }
  if (course.session_start_time || course.session_end_time) {
    parts.push(
      `${course.session_start_time || ''}–${course.session_end_time || ''}`.replace(/^–|–$/g, '')
    );
  }
  return parts.length ? parts.join(' · ') : null;
}

function formatOutlineItemType(/** @type {string|undefined} */ t) {
  if (!t) return 'Item';
  if (t === 'live_video_call') return 'Live session';
  return String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function PublicTrainingCourseDetailBlock({
  course,
  summary,
  feedbackEligibility,
  feedbackRating,
  onFeedbackRatingChange,
  feedbackThumb,
  onFeedbackThumbChange,
  feedbackComment,
  onFeedbackCommentChange,
  onSubmitFeedback,
  feedbackSubmitting,
  feedbackMessage,
  onSelectVersion
}) {
  if (!course) return null;
  const el = feedbackEligibility;
  const showForm = el?.authenticated && el?.completed && el?.canSubmit;
  const my = el?.myFeedback;
  const scheduleText = formatCourseSchedule(course);
  const instructorNames = (course.instructors || [])
    .map((i) => i.name || `${i.first_name || ''} ${i.last_name || ''}`.trim())
    .filter(Boolean);
  const versions = Array.isArray(course.versions) ? course.versions : [];
  const outline = Array.isArray(course.outline) ? course.outline : [];
  const deliverables = Array.isArray(course.deliverables) ? course.deliverables : [];

  return (
    <>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 1 }}>
        <Chip
          size="small"
          label={course.category?.name || 'Category'}
          sx={{
            fontWeight: 700,
            bgcolor: TRAINING_HIERARCHY_COLORS.category.bg,
            border: `1px solid ${TRAINING_HIERARCHY_COLORS.category.border}`,
            color: TRAINING_HIERARCHY_COLORS.category.text
          }}
        />
        <Chip
          size="small"
          label={course.learningSpot?.name || 'Sub category'}
          sx={{
            fontWeight: 700,
            bgcolor: TRAINING_HIERARCHY_COLORS.subcategory.bg,
            border: `1px solid ${TRAINING_HIERARCHY_COLORS.subcategory.border}`,
            color: TRAINING_HIERARCHY_COLORS.subcategory.text
          }}
        />
        <Chip
          size="small"
          label={course.name}
          sx={{
            fontWeight: 700,
            bgcolor: TRAINING_HIERARCHY_COLORS.course.bg,
            border: `1px solid ${TRAINING_HIERARCHY_COLORS.course.border}`,
            color: TRAINING_HIERARCHY_COLORS.course.text
          }}
        />
        <Chip
          size="small"
          label={course.edition_label || 'Version'}
          sx={{
            fontWeight: 700,
            bgcolor: TRAINING_HIERARCHY_COLORS.version.bg,
            border: `1px solid ${TRAINING_HIERARCHY_COLORS.version.border}`,
            color: TRAINING_HIERARCHY_COLORS.version.text
          }}
        />
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {course.name}
        {course.edition_label ? ` (${course.edition_label})` : ''}
      </Typography>

      {versions.length > 1 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
            Versions:
          </Typography>
          {versions.map((v) => (
            <Chip
              key={v.id}
              size="small"
              color={v.isCurrent || String(v.id) === String(course.id) ? 'primary' : 'default'}
              variant={v.isCurrent || String(v.id) === String(course.id) ? 'filled' : 'outlined'}
              label={v.edition_label || v.course_code || 'Version'}
              onClick={
                onSelectVersion && String(v.id) !== String(course.id)
                  ? () => onSelectVersion(v.id)
                  : undefined
              }
              clickable={!!onSelectVersion && String(v.id) !== String(course.id)}
            />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip size="small" label={`Format: ${formatDeliveryType(course.delivery_type)}`} />
        {course.outline_format && (
          <Chip size="small" variant="outlined" label={`Outline: ${formatOutlineFormat(course.outline_format)}`} />
        )}
        {course.schedule_model && course.schedule_model !== 'none' && (
          <Chip size="small" variant="outlined" label={`Schedule: ${formatOutlineFormat(course.schedule_model)}`} />
        )}
        {course.course_code && <Chip size="small" variant="outlined" label={course.course_code} />}
        {course.edition_label && <Chip size="small" color="secondary" variant="outlined" label={course.edition_label} />}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
          gap: 1,
          mb: 2,
          '& .label': { color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }
        }}
      >
        <Typography className="label">Instructor(s)</Typography>
        <Typography variant="body2">{instructorNames.length ? instructorNames.join(', ') : '—'}</Typography>
        <Typography className="label">Start date</Typography>
        <Typography variant="body2">{course.start_date || '—'}</Typography>
        <Typography className="label">End date</Typography>
        <Typography variant="body2">{course.end_date || '—'}</Typography>
        <Typography className="label">Schedule</Typography>
        <Typography variant="body2">{scheduleText || '—'}</Typography>
        <Typography className="label">Delivery format</Typography>
        <Typography variant="body2">{formatDeliveryType(course.delivery_type)}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Course outline
      </Typography>
      {outline.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No outline published for this version yet.
        </Typography>
      ) : (
        <List dense sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflow: 'auto' }}>
          {outline.map((row) => (
            <ListItemText
              key={`${row.order}-${row.item_type}-${row.summary}`}
              sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
                    {row.order}.
                  </Typography>
                  <Chip size="small" label={formatOutlineItemType(row.item_type)} sx={{ height: 22 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {row.summary || row.label || '—'}
                  </Typography>
                  {row.schedule_label && (
                    <Typography variant="caption" color="text.secondary">
                      ({row.schedule_label})
                    </Typography>
                  )}
                </Stack>
              }
              secondary={row.notes || undefined}
            />
          ))}
        </List>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Deliverables
      </Typography>
      {deliverables.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No material or exam deliverables listed in the outline.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {deliverables.map((d, i) => (
            <Typography key={`${d.type}-${d.name}-${i}`} variant="body2">
              • [{formatOutlineItemType(d.type)}] {d.name}
              {d.content_type ? ` · ${d.content_type}` : ''}
              {d.notes ? ` — ${d.notes}` : ''}
            </Typography>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Course feedback
      </Typography>
      {summary && summary.count > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Approved average:{' '}
          <strong>{summary.avgRating != null ? `${summary.avgRating} / 5` : '—'}</strong> ({summary.count}{' '}
          {summary.count === 1 ? 'review' : 'reviews'})
        </Typography>
      )}
      {Array.isArray(summary?.items) && summary.items.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {summary.items.slice(0, 8).map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Rating value={Number(item.rating) || 0} readOnly size="small" />
                {item.thumb === 'up' && <ThumbUpAltIcon fontSize="small" color="success" />}
                {item.thumb === 'down' && <ThumbDownAltIcon fontSize="small" color="error" />}
                <Typography variant="caption" color="text.secondary">
                  {item.submitter_name} · Approved
                </Typography>
              </Stack>
              {item.comment && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {item.comment}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      )}
      {el?.loading ? (
        <Box sx={{ py: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : el?.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load feedback eligibility. Try signing in again.
        </Alert>
      ) : !el?.authenticated ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sign in, enroll, and complete this course to leave a 5★ review (optional thumbs + comment). Instructors approve
          feedback before it appears under the course.
        </Alert>
      ) : !el?.completed ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Complete all required outline items for this course, then return here to submit feedback.
        </Alert>
      ) : my?.review_status === 'pending' || el?.reason === 'pending_review' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Status: <strong>Submitted</strong> — waiting for instructor approval.
        </Alert>
      ) : my?.review_status === 'accepted' || el?.reason === 'already_accepted' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Status: <strong>Approved</strong> — your feedback is published under this course.
        </Alert>
      ) : null}
      {showForm && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Your rating (1–5 stars)
          </Typography>
          <Rating
            value={Number(feedbackRating) || 0}
            onChange={(e, v) => onFeedbackRatingChange(v || 1)}
            size="large"
            sx={{ mb: 1.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Thumbs (optional)
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={feedbackThumb || null}
            onChange={(e, v) => onFeedbackThumbChange(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="up" aria-label="Thumbs up">
              {feedbackThumb === 'up' ? <ThumbUpAltIcon /> : <ThumbUpAltOutlinedIcon />}
            </ToggleButton>
            <ToggleButton value="down" aria-label="Thumbs down">
              {feedbackThumb === 'down' ? <ThumbDownAltIcon /> : <ThumbDownAltOutlinedIcon />}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Comments (optional)"
            placeholder="What worked well? What could improve?"
            value={feedbackComment}
            onChange={(e) => onFeedbackCommentChange(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
          />
          {feedbackMessage && (
            <Alert severity={feedbackMessage.type === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
              {feedbackMessage.text}
            </Alert>
          )}
          <Button variant="outlined" onClick={onSubmitFeedback} disabled={feedbackSubmitting} sx={{ mb: 2 }}>
            {feedbackSubmitting ? 'Submitting…' : 'Submit feedback'}
          </Button>
        </>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Enroll
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create an account or sign in to enroll in this course after you have reviewed the details above.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button component={RouterLink} to="/signup" variant="contained">
          Sign up to enroll
        </Button>
        <Button component={RouterLink} to="/login">
          Log in
        </Button>
      </Stack>
    </>
  );
}

function PublicExamDetailBlock({ exam }) {
  if (!exam) return null;
  return (
    <>
      <Typography variant="overline" color="text.secondary">
        {exam.category?.name || 'Exam'}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {exam.name}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {exam.time_limit_minutes != null && <Chip size="small" label={`${exam.time_limit_minutes} min`} />}
        {exam.pass_score_percent != null && (
          <Chip size="small" variant="outlined" label={`Pass ${exam.pass_score_percent}%`} />
        )}
      </Stack>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
        {exam.description || '—'}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Take this exam
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign in to your workspace and open Trainings → Exams to start this assessment.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button component={RouterLink} to="/signup" variant="contained">
          Sign up
        </Button>
        <Button component={RouterLink} to="/login">
          Log in
        </Button>
      </Stack>
    </>
  );
}

function SplitMorePreviewPanel({ splitTab }) {
  if (splitTab === 'more') {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          More coming
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Additional public resources will appear here as your organization enables them.
        </Typography>
      </Box>
    );
  }

  if (splitTab === 'jobs') {
    return null;
  }

  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography color="text.secondary">Select an item in the list on the left to preview it here.</Typography>
    </Box>
  );
}

const PublicPortalPage = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const {
    layoutMode,
    contentPanelTheme,
    publicBranding,
    splitBrowsePanelTheme,
    splitPreviewPanelTheme
  } = usePublicPortalAppearance();

  const panelCardSx = useMemo(
    () => ({
      bgcolor: contentPanelTheme.panelBg,
      borderColor: contentPanelTheme.panelBorder,
      boxShadow: contentPanelTheme.panelShadow,
      border: '1px solid',
      borderStyle: 'solid',
      color: contentPanelTheme.textPrimary
    }),
    [contentPanelTheme]
  );
  const panelTextScopeSx = useMemo(
    () => ({
      color: contentPanelTheme.textPrimary,
      '& .MuiTypography-root': { color: 'inherit' },
      '& .MuiTypography-colorTextSecondary, & .MuiFormHelperText-root, & .MuiInputLabel-root': {
        color: `${contentPanelTheme.textSecondary} !important`
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: contentPanelTheme.inputBg,
        color: contentPanelTheme.textPrimary,
        '& fieldset': { borderColor: contentPanelTheme.panelBorder },
        '&:hover fieldset': { borderColor: contentPanelTheme.textSecondary }
      },
      '& .MuiSelect-icon, & .MuiSvgIcon-root.MuiChip-deleteIcon': {
        color: contentPanelTheme.textSecondary
      },
      '& .MuiChip-root': {
        bgcolor: contentPanelTheme.inputBg,
        color: contentPanelTheme.textPrimary,
        borderColor: contentPanelTheme.panelBorder
      }
    }),
    [contentPanelTheme]
  );

  const splitBrowsePanelSurfaceSx = useMemo(
    () => ({
      bgcolor: splitBrowsePanelTheme.panelBg,
      borderColor: splitBrowsePanelTheme.panelBorder,
      boxShadow: splitBrowsePanelTheme.panelShadow,
      // Keep the left browse column visually distinct from the page background
      outline: `1px solid ${splitBrowsePanelTheme.panelBorder || 'rgba(148,163,184,0.45)'}`,
      outlineOffset: -1,
      zIndex: 1,
    }),
    [splitBrowsePanelTheme]
  );
  const splitBrowsePanelTextScopeSx = useMemo(
    () => ({
      color: splitBrowsePanelTheme.textPrimary,
      '& .MuiTypography-root': { color: 'inherit' },
      '& .MuiTypography-colorTextSecondary, & .MuiFormHelperText-root, & .MuiInputLabel-root': {
        color: `${splitBrowsePanelTheme.textSecondary} !important`
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: splitBrowsePanelTheme.inputBg,
        color: splitBrowsePanelTheme.textPrimary,
        '& fieldset': { borderColor: splitBrowsePanelTheme.panelBorder }
      }
    }),
    [splitBrowsePanelTheme]
  );

  const splitPreviewPanelCardSx = useMemo(
    () => ({
      bgcolor: splitPreviewPanelTheme.panelBg,
      borderColor: splitPreviewPanelTheme.panelBorder,
      boxShadow: splitPreviewPanelTheme.panelShadow,
      border: '1px solid',
      borderStyle: 'solid',
      color: splitPreviewPanelTheme.textPrimary
    }),
    [splitPreviewPanelTheme]
  );
  const splitPreviewPanelTextScopeSx = useMemo(
    () => ({
      color: splitPreviewPanelTheme.textPrimary,
      '& .MuiTypography-root': { color: 'inherit' },
      '& .MuiTypography-colorTextSecondary, & .MuiFormHelperText-root, & .MuiInputLabel-root': {
        color: `${splitPreviewPanelTheme.textSecondary} !important`
      },
      '& .MuiOutlinedInput-root': {
        bgcolor: splitPreviewPanelTheme.inputBg,
        color: splitPreviewPanelTheme.textPrimary,
        '& fieldset': { borderColor: splitPreviewPanelTheme.panelBorder }
      }
    }),
    [splitPreviewPanelTheme]
  );

  const [loading, setLoading] = useState(true);
  const [vis, setVis] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [training, setTraining] = useState([]);
  const [exams, setExams] = useState([]);
  const [publicBenches, setPublicBenches] = useState([]);
  const [loadError, setLoadError] = useState('');

  const [splitTab, setSplitTab] = useState('jobs');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [selectedBenchId, setSelectedBenchId] = useState(null);
  const splitDefaultsAppliedRef = useRef(false);
  const splitContainerRef = useRef(null);
  const splitLeftPctRef = useRef(getStoredSplitRatio() * 100);
  const [splitLeftPct, setSplitLeftPct] = useState(() => getStoredSplitRatio() * 100);
  // Always show browse list when opening Query page (persisted "hidden" made the left pane look missing).
  const [splitListHidden, setSplitListHidden] = useState(false);
  useEffect(() => {
    setStoredSplitListHidden(false);
  }, []);
  splitLeftPctRef.current = splitLeftPct;

  const [jobFilters, setJobFilters] = useState({
    search: '',
    location: '',
    client: '',
    remote: 'any'
  });
  /** 'browse' = filters + list on the right; 'detail' = full job in frame */
  const [jobSplitView, setJobSplitView] = useState('browse');
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [trainingViewMode, setTrainingViewMode] = useState(/** @type {'category' | 'course'} */ ('category'));
  const [selectedTrainingCourseId, setSelectedTrainingCourseId] = useState(null);
  const [detailTrainingCourse, setDetailTrainingCourse] = useState(null);
  const [detailTrainingLoading, setDetailTrainingLoading] = useState(false);
  const [trainingFeedbackSummary, setTrainingFeedbackSummary] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackThumb, setFeedbackThumb] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const [detailExam, setDetailExam] = useState(null);
  const [detailExamLoading, setDetailExamLoading] = useState(false);

  const [trainingDialogCourseId, setTrainingDialogCourseId] = useState(null);
  const [dialogTrainingCourse, setDialogTrainingCourse] = useState(null);
  const [dialogTrainingLoading, setDialogTrainingLoading] = useState(false);
  const [dialogTrainingFeedbackSummary, setDialogTrainingFeedbackSummary] = useState(null);
  const [dialogFeedbackRating, setDialogFeedbackRating] = useState(5);
  const [dialogFeedbackThumb, setDialogFeedbackThumb] = useState(null);
  const [dialogFeedbackComment, setDialogFeedbackComment] = useState('');
  const [dialogFeedbackSubmitting, setDialogFeedbackSubmitting] = useState(false);
  const [dialogFeedbackMessage, setDialogFeedbackMessage] = useState(null);

  const [examDialogId, setExamDialogId] = useState(null);
  const [dialogExam, setDialogExam] = useState(null);
  const [dialogExamLoading, setDialogExamLoading] = useState(false);

  const [trainingBrowseScope, setTrainingBrowseScope] = useState(/** @type {'all' | 'category'} */ ('all'));
  const [trainingLayoutFormat, setTrainingLayoutFormat] = useState(/** @type {'list' | 'box'} */ (() => getStoredTrainingLayoutFormat()));
  const [trainingFilters, setTrainingFilters] = useState({
    search: '',
    categoryId: '',
    delivery: 'any',
    learningSpotId: ''
  });
  const [examFilters, setExamFilters] = useState({
    search: '',
    timeLimit: 'any',
    passAtLeast: ''
  });
  const [feedbackEligibility, setFeedbackEligibility] = useState(null);
  const [dialogFeedbackEligibility, setDialogFeedbackEligibility] = useState(null);

  const portalBase =
    location.pathname.startsWith('/query') || location.pathname.startsWith('/public') ? '/query' : '/query';
  const sectionHref = (id) => `${portalBase}#${id}`;
  const jobsSectionPath = portalBase;

  const filteredJobs = useMemo(() => {
    if (!jobs.length) return [];
    return jobs.filter((j) => {
      const q = jobFilters.search.trim().toLowerCase();
      if (q) {
        const blob = [
          j.title,
          j.job_code,
          j.description_external,
          j.skills_required,
          j.skills_preferred,
          j.client?.name,
          j.client?.company_name,
          j.location,
          j.city,
          j.state,
          j.country,
          j.employment_type
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      const loc = jobFilters.location.trim().toLowerCase();
      if (loc) {
        const blob = [j.location, j.city, j.state, j.country].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(loc)) return false;
      }
      const cl = jobFilters.client.trim().toLowerCase();
      if (cl) {
        const name = (j.client?.name || j.client?.company_name || '').toLowerCase();
        if (!name.includes(cl)) return false;
      }
      if (!matchRemoteOption(jobFilters.remote, j.remote_option)) return false;
      return true;
    });
  }, [jobs, jobFilters]);

  const trainingRows = useMemo(
    () =>
      training
        .map((c) => ({
          ...c,
          // Count every published edition/version (not unique course groups).
          publishedVersionCount: countPublishedVersions(c)
        }))
        .filter((c) => (c.publishedVersionCount || 0) > 0),
    [training]
  );

  const allTrainingCoursesFlat = useMemo(() => {
    const out = [];
    for (const cat of trainingRows) {
      for (const c of cat.publishedCourses || []) {
        out.push({
          ...c,
          categoryId: cat.id,
          categoryName: cat.name
        });
      }
    }
    return out;
  }, [trainingRows]);

  const filteredAllTrainingCourses = useMemo(() => {
    let list = allTrainingCoursesFlat;
    const q = trainingFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const blob = [c.name, c.categoryName, c.subCategoryName, c.delivery_type, c.edition_label, c.course_code]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      });
    }
    if (trainingFilters.categoryId) {
      list = list.filter((c) => c.categoryId === trainingFilters.categoryId);
    }
    if (trainingFilters.delivery !== 'any') {
      list = list.filter((c) => c.delivery_type === trainingFilters.delivery);
    }
    if (trainingFilters.learningSpotId) {
      list = list.filter((c) => c.subCategoryId === trainingFilters.learningSpotId);
    }
    return list;
  }, [allTrainingCoursesFlat, trainingFilters]);

  /** Hierarchy for All / category browse: categories → spots → courses → versions. */
  const filteredTrainingHierarchy = useMemo(() => {
    if (trainingBrowseScope === 'category' && selectedTrainingId) {
      const cat = trainingRows.find((c) => c.id === selectedTrainingId);
      if (!cat) return [];
      return [
        {
          id: cat.id,
          name: cat.name,
          description: cat.description,
          groups: groupCoursesByLearningSpot(cat.publishedCourses || [])
        }
      ];
    }
    const allowedIds = new Set(filteredAllTrainingCourses.map((c) => c.id));
    return trainingRows
      .map((cat) => {
        const versions = (cat.publishedCourses || []).filter((c) => allowedIds.has(c.id));
        if (!versions.length) return null;
        return {
          id: cat.id,
          name: cat.name,
          description: cat.description,
          groups: groupCoursesByLearningSpot(versions)
        };
      })
      .filter(Boolean);
  }, [trainingRows, filteredAllTrainingCourses, trainingBrowseScope, selectedTrainingId]);

  const learningSpotOptions = useMemo(() => {
    const m = new Map();
    for (const c of allTrainingCoursesFlat) {
      if (c.subCategoryId && c.subCategoryName) {
        m.set(c.subCategoryId, c.subCategoryName);
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [allTrainingCoursesFlat]);

  const filteredExams = useMemo(() => {
    if (!exams.length) return [];
    let list = exams;
    const q = examFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter((ex) => {
        const blob = [ex.name, ex.description, ex.category?.name].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    if (examFilters.timeLimit === 'short') {
      list = list.filter((ex) => ex.time_limit_minutes != null && ex.time_limit_minutes <= 45);
    } else if (examFilters.timeLimit === 'long') {
      list = list.filter((ex) => ex.time_limit_minutes == null || ex.time_limit_minutes > 45);
    }
    const minPass = parseInt(examFilters.passAtLeast, 10);
    if (!Number.isNaN(minPass) && examFilters.passAtLeast !== '') {
      list = list.filter((ex) => ex.pass_score_percent != null && ex.pass_score_percent >= minPass);
    }
    return list;
  }, [exams, examFilters]);

  useEffect(() => {
    const base = getPublicApiBase();
    let cancelled = false;
    (async () => {
      try {
        setLoadError('');
        const [vRes, jRes, tRes, eRes] = await Promise.all([
          axios.get(`${base}/visibility`),
          axios.get(`${base}/jobs`),
          axios.get(`${base}/training/catalog`),
          axios.get(`${base}/exams`)
        ]);
        if (cancelled) return;
        const v = vRes.data?.data || {};
        setVis({ training: v.training, jobs: v.jobs, exams: v.exams, benches: v.benches });
        setJobs(jRes.data?.data?.jobs || []);
        setTraining(tRes.data?.data?.categories || []);
        setExams(eRes.data?.data?.exams || []);
        if (v.benches === 'public') {
          try {
            const benchRes = await axios.get(`${base}/bench/catalog`);
            setPublicBenches(benchRes.data?.data?.benches || []);
          } catch {
            setPublicBenches([]);
          }
        } else {
          setPublicBenches([]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setVis({ training: 'private', jobs: 'private', exams: 'private', benches: 'private' });
          setLoadError(
            e.response?.data?.message ||
              (e.code === 'ERR_NETWORK' ? 'Cannot reach the API. Check that the backend is running and the dev proxy targets it.' : '') ||
              'Could not load public content.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    const h = location.hash;
    if (!h || h.length < 2) return;
    const t = window.setTimeout(() => scrollToHash(h), 140);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  const jobsPublic = vis?.jobs === 'public';
  const trainingPublic = vis?.training === 'public';
  const examsPublic = vis?.exams === 'public';
  const benchesPublic = vis?.benches === 'public';
  const anyPublic = jobsPublic || trainingPublic || examsPublic || benchesPublic;

  useEffect(() => {
    if (layoutMode === 'regular') {
      splitDefaultsAppliedRef.current = false;
    }
  }, [layoutMode]);

  useEffect(() => {
    if (loading || layoutMode !== 'split') return;
    const hash = (location.hash || '').replace('#', '');

    const mTrainCat = hash.match(/^portal-training-cat-(.+)$/);
    if (mTrainCat && trainingPublic) {
      if (trainingRows.length === 0) return;
      const rawId = mTrainCat[1];
      const cat = trainingRows.find((c) => String(c.id) === String(rawId));
      setSplitTab('training');
      setSelectedTrainingId(cat ? cat.id : rawId);
      setTrainingBrowseScope('category');
      setTrainingViewMode('category');
      splitDefaultsAppliedRef.current = true;
      return;
    }

    if (hash === 'portal-training' && trainingPublic) {
      setSplitTab('training');
      setTrainingBrowseScope('all');
      setSelectedTrainingId(null);
      setTrainingViewMode('category');
      setSelectedTrainingCourseId(null);
      splitDefaultsAppliedRef.current = true;
      return;
    }
    if (hash === 'portal-jobs' && jobsPublic) {
      setSplitTab('jobs');
      splitDefaultsAppliedRef.current = true;
      return;
    }
    if (hash === 'portal-exams' && examsPublic) {
      setSplitTab('exams');
      splitDefaultsAppliedRef.current = true;
      return;
    }
    if (hash === 'portal-benches' && benchesPublic) {
      setSplitTab('benches');
      if (publicBenches.length) setSelectedBenchId(publicBenches[0].id);
      splitDefaultsAppliedRef.current = true;
      return;
    }

    if (splitDefaultsAppliedRef.current) return;
    splitDefaultsAppliedRef.current = true;
    let defaultHash = 'portal-more';
    if (jobsPublic && jobs.length) {
      setSplitTab('jobs');
      setSelectedJobId(jobs[0].id);
      defaultHash = 'portal-jobs';
    } else if (trainingPublic && trainingRows.length) {
      setSplitTab('training');
      setTrainingBrowseScope('all');
      setSelectedTrainingId(null);
      defaultHash = 'portal-training';
    } else if (examsPublic && exams.length) {
      setSplitTab('exams');
      setSelectedExamId(exams[0].id);
      defaultHash = 'portal-exams';
    } else if (benchesPublic && publicBenches.length) {
      setSplitTab('benches');
      setSelectedBenchId(publicBenches[0].id);
      defaultHash = 'portal-benches';
    } else {
      setSplitTab('more');
    }
    // Keep top section menu sparkle in sync when opening browsing without a hash.
    if (!hash) {
      navigate(`${portalBase}#${defaultHash}`, { replace: true });
    }
  }, [
    loading,
    layoutMode,
    location.hash,
    jobsPublic,
    trainingPublic,
    examsPublic,
    benchesPublic,
    jobs,
    trainingRows,
    exams,
    publicBenches,
    portalBase,
    navigate
  ]);

  const openJobDetailInFrame = useCallback((id) => {
    setSelectedJobId(id);
    setJobSplitView('detail');
  }, []);

  useEffect(() => {
    if (splitTab !== 'jobs') {
      setJobSplitView('browse');
      setDetailJob(null);
    }
  }, [splitTab]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'jobs' || jobSplitView !== 'detail' || !selectedJobId) {
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    axios
      .get(publicApiUrl(`/jobs/${selectedJobId}`))
      .then((r) => {
        if (!cancelled) setDetailJob(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDetailJob(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, jobSplitView, selectedJobId]);

  useEffect(() => {
    if (splitTab !== 'training') {
      setTrainingViewMode('category');
      setSelectedTrainingCourseId(null);
      setDetailTrainingCourse(null);
      setTrainingFeedbackSummary(null);
      setFeedbackMessage(null);
      setTrainingBrowseScope('all');
      setFeedbackEligibility(null);
    }
  }, [splitTab]);

  useEffect(() => {
    setTrainingViewMode('category');
    setSelectedTrainingCourseId(null);
    setDetailTrainingCourse(null);
    setTrainingFeedbackSummary(null);
    setFeedbackMessage(null);
  }, [selectedTrainingId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'training' || trainingViewMode !== 'course' || !selectedTrainingCourseId) {
      return;
    }
    let cancelled = false;
    setDetailTrainingLoading(true);
    setFeedbackMessage(null);
    setFeedbackRating(5);
    setFeedbackThumb(null);
    setFeedbackComment('');
    Promise.all([
      axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}`)),
      axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}/feedback/summary`))
    ])
      .then(([cRes, sRes]) => {
        if (cancelled) return;
        setDetailTrainingCourse(cRes.data?.data || null);
        setTrainingFeedbackSummary(sRes.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) {
          setDetailTrainingCourse(null);
          setTrainingFeedbackSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailTrainingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, trainingViewMode, selectedTrainingCourseId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'training' || trainingViewMode !== 'course' || !selectedTrainingCourseId) {
      setFeedbackEligibility(null);
      return;
    }
    const token = getAuthAccessToken();
    if (!token) {
      setFeedbackEligibility({ loading: false, authenticated: false });
      return;
    }
    let cancelled = false;
    setFeedbackEligibility({ loading: true, authenticated: true });
    api
      .get(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback/eligibility`)
      .then((r) => {
        if (!cancelled) setFeedbackEligibility({ loading: false, authenticated: true, ...r.data?.data });
      })
      .catch(() => {
        if (!cancelled) setFeedbackEligibility({ loading: false, authenticated: true, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, trainingViewMode, selectedTrainingCourseId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'exams' || !selectedExamId) {
      return;
    }
    let cancelled = false;
    setDetailExamLoading(true);
    axios
      .get(publicApiUrl(`/exams/${selectedExamId}`))
      .then((r) => {
        if (!cancelled) setDetailExam(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDetailExam(null);
      })
      .finally(() => {
        if (!cancelled) setDetailExamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, selectedExamId]);

  useEffect(() => {
    if (!trainingDialogCourseId) {
      setDialogTrainingCourse(null);
      setDialogTrainingFeedbackSummary(null);
      setDialogFeedbackMessage(null);
      return;
    }
    let cancelled = false;
    setDialogTrainingLoading(true);
    setDialogFeedbackRating(5);
    setDialogFeedbackThumb(null);
    setDialogFeedbackComment('');
    setDialogFeedbackMessage(null);
    Promise.all([
      axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}`)),
      axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}/feedback/summary`))
    ])
      .then(([cRes, sRes]) => {
        if (cancelled) return;
        setDialogTrainingCourse(cRes.data?.data || null);
        setDialogTrainingFeedbackSummary(sRes.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) {
          setDialogTrainingCourse(null);
          setDialogTrainingFeedbackSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDialogTrainingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trainingDialogCourseId]);

  useEffect(() => {
    if (!trainingDialogCourseId) {
      setDialogFeedbackEligibility(null);
      return;
    }
    const token = getAuthAccessToken();
    if (!token) {
      setDialogFeedbackEligibility({ loading: false, authenticated: false });
      return;
    }
    let cancelled = false;
    setDialogFeedbackEligibility({ loading: true, authenticated: true });
    api
      .get(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback/eligibility`)
      .then((r) => {
        if (!cancelled) setDialogFeedbackEligibility({ loading: false, authenticated: true, ...r.data?.data });
      })
      .catch(() => {
        if (!cancelled) setDialogFeedbackEligibility({ loading: false, authenticated: true, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [trainingDialogCourseId]);

  useEffect(() => {
    if (!examDialogId) {
      setDialogExam(null);
      return;
    }
    let cancelled = false;
    setDialogExamLoading(true);
    axios
      .get(publicApiUrl(`/exams/${examDialogId}`))
      .then((r) => {
        if (!cancelled) setDialogExam(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDialogExam(null);
      })
      .finally(() => {
        if (!cancelled) setDialogExamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examDialogId]);

  useEffect(() => {
    if (splitTab !== 'jobs') return;
    if (filteredJobs.length === 0) {
      setSelectedJobId(null);
      setJobSplitView('browse');
      setDetailJob(null);
      return;
    }
    if (!selectedJobId) return;
    if (!filteredJobs.some((j) => j.id === selectedJobId)) {
      setSelectedJobId(filteredJobs[0].id);
      setJobSplitView('browse');
      setDetailJob(null);
    }
  }, [filteredJobs, splitTab, selectedJobId]);

  useEffect(() => {
    if (splitTab !== 'exams') return;
    if (filteredExams.length === 0) {
      setSelectedExamId(null);
      return;
    }
    if (!selectedExamId || !filteredExams.some((e) => e.id === selectedExamId)) {
      setSelectedExamId(filteredExams[0].id);
    }
  }, [filteredExams, splitTab, selectedExamId]);

  const onSplitTabChange = useCallback(
    (tab) => {
      setSplitTab(tab);
      const hashByTab = {
        jobs: 'portal-jobs',
        training: 'portal-training',
        exams: 'portal-exams',
        benches: 'portal-benches',
        more: 'portal-more'
      };
      const nextHash = hashByTab[tab];
      if (nextHash) {
        const next = `${portalBase}#${nextHash}`;
        const current = `${location.pathname}${location.hash || ''}`;
        if (current !== next) {
          navigate(next, { replace: true });
        }
      }
      if (tab === 'jobs' && jobsPublic && jobs.length) {
        setJobSplitView('browse');
        setDetailJob(null);
        setSelectedJobId(jobs[0].id);
        setSelectedTrainingId(null);
        setSelectedExamId(null);
        setSelectedBenchId(null);
      } else if (tab === 'training' && trainingPublic && trainingRows.length) {
        setTrainingBrowseScope('all');
        setSelectedTrainingId(null);
        setTrainingViewMode('category');
        setSelectedTrainingCourseId(null);
        setSelectedJobId(null);
        setSelectedExamId(null);
        setSelectedBenchId(null);
      } else if (tab === 'exams' && examsPublic && exams.length) {
        setExamFilters({ search: '', timeLimit: 'any', passAtLeast: '' });
        setSelectedExamId(exams[0].id);
        setSelectedJobId(null);
        setSelectedTrainingId(null);
        setSelectedBenchId(null);
      } else if (tab === 'benches' && benchesPublic && publicBenches.length) {
        setSelectedBenchId(publicBenches[0].id);
        setSelectedJobId(null);
        setSelectedTrainingId(null);
        setSelectedExamId(null);
      } else {
        setSelectedJobId(null);
        setSelectedTrainingId(null);
        setSelectedExamId(null);
        setSelectedBenchId(null);
      }
    },
    [
      jobsPublic,
      trainingPublic,
      examsPublic,
      benchesPublic,
      jobs,
      trainingRows,
      exams,
      publicBenches,
      portalBase,
      navigate,
      location.pathname,
      location.hash
    ]
  );

  const submitSplitTrainingFeedback = useCallback(async () => {
    if (!selectedTrainingCourseId) return;
    setFeedbackSubmitting(true);
    setFeedbackMessage(null);
    try {
      const res = await api.post(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback`, {
        rating: feedbackRating,
        thumb: feedbackThumb || undefined,
        comment: feedbackComment.trim() || undefined
      });
      setFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been submitted for instructor review (status: Submitted).'
      });
      setFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been sent to the course owner for review.'
      });
      setFeedbackComment('');
      const [sRes, elRes] = await Promise.all([
        axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}/feedback/summary`)),
        api.get(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback/eligibility`)
      ]);
      setTrainingFeedbackSummary(sRes.data?.data || null);
      setFeedbackEligibility({ loading: false, authenticated: true, ...elRes.data?.data });
    } catch (e) {
      setFeedbackMessage({
        type: 'error',
        text: e.response?.data?.message || 'Could not save feedback.'
      });
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [selectedTrainingCourseId, feedbackRating, feedbackThumb, feedbackComment]);

  const submitDialogTrainingFeedback = useCallback(async () => {
    if (!trainingDialogCourseId) return;
    setDialogFeedbackSubmitting(true);
    setDialogFeedbackMessage(null);
    try {
      const res = await api.post(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback`, {
        rating: dialogFeedbackRating,
        thumb: dialogFeedbackThumb || undefined,
        comment: dialogFeedbackComment.trim() || undefined
      });
      setDialogFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been submitted for instructor review (status: Submitted).'
      });
      setDialogFeedbackComment('');
      setDialogFeedbackThumb(null);
      const [sRes, elRes] = await Promise.all([
        axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}/feedback/summary`)),
        api.get(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback/eligibility`)
      ]);
      setDialogTrainingFeedbackSummary(sRes.data?.data || null);
      setDialogFeedbackEligibility({ loading: false, authenticated: true, ...elRes.data?.data });
    } catch (e) {
      setDialogFeedbackMessage({
        type: 'error',
        text: e.response?.data?.message || 'Could not save feedback.'
      });
    } finally {
      setDialogFeedbackSubmitting(false);
    }
  }, [trainingDialogCourseId, dialogFeedbackRating, dialogFeedbackThumb, dialogFeedbackComment]);

  const handleSplitDividerPointerDown = useCallback((e) => {
    if (splitListHidden) return;
    e.preventDefault();
    const el = splitContainerRef.current;
    if (!el) return;

    const pointerMove = (ev) => {
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const w = rect.width;
      if (w < 40) return;
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const x = clientX - rect.left;
      const pct = (x / w) * 100;
      const clamped = Math.min(48, Math.max(18, pct));
      splitLeftPctRef.current = clamped;
      setSplitLeftPct(clamped);
    };

    const pointerUp = () => {
      window.removeEventListener('mousemove', pointerMove);
      window.removeEventListener('mouseup', pointerUp);
      window.removeEventListener('touchmove', pointerMove);
      window.removeEventListener('touchend', pointerUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      setStoredSplitRatio(splitLeftPctRef.current / 100);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', pointerMove);
    window.addEventListener('mouseup', pointerUp);
    window.addEventListener('touchmove', pointerMove, { passive: false });
    window.addEventListener('touchend', pointerUp);
  }, [splitListHidden]);

  const hideSplitList = useCallback(() => {
    setSplitListHidden(true);
    setStoredSplitListHidden(true);
  }, []);

  const showSplitList = useCallback(() => {
    setSplitListHidden(false);
    setStoredSplitListHidden(false);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const alertsBlock = (
    <>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {!anyPublic && !loadError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Public listings are not enabled yet. Please log in to access your workspace, or ask an administrator to enable
          visibility under Administration → Visibility setup (set Training / Jobs / Exams / Benches to Public as needed).
        </Alert>
      )}
      {!loadError && jobsPublic && jobs.length === 0 && layoutMode === 'regular' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No jobs match the public list yet. Jobs must be <strong>Active</strong>, set to <strong>public listing</strong>{' '}
          on the job, and the tenant must have <strong>Jobs</strong> set to <strong>Public</strong> under Visibility setup.
        </Alert>
      )}
    </>
  );

  const regularSections = (
    <Box sx={panelTextScopeSx}>
      <Box id="portal-jobs" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <WorkIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={`${jobsSectionPath}#portal-jobs`}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: contentPanelTheme.textPrimary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Open positions
          </Typography>
        </Stack>
        {!jobsPublic ? (
          <Typography color="text.secondary">Job listings are available only to signed-in users.</Typography>
        ) : jobs.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ py: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">No public job postings right now. Check back soon.</Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField
                size="small"
                label="Search"
                placeholder="Title, skills, description, job code…"
                value={jobFilters.search}
                onChange={(e) => setJobFilters((f) => ({ ...f, search: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  label="Location"
                  placeholder="City, state, country…"
                  value={jobFilters.location}
                  onChange={(e) => setJobFilters((f) => ({ ...f, location: e.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Client / company"
                  value={jobFilters.client}
                  onChange={(e) => setJobFilters((f) => ({ ...f, client: e.target.value }))}
                  fullWidth
                />
                <FormControl size="small" fullWidth sx={{ minWidth: 160 }}>
                  <InputLabel>Work arrangement</InputLabel>
                  <Select
                    label="Work arrangement"
                    value={jobFilters.remote}
                    onChange={(e) => setJobFilters((f) => ({ ...f, remote: e.target.value }))}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    <MenuItem value="remote">Remote</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                    <MenuItem value="onsite">On-site</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredJobs.length} of {jobs.length} roles
              </Typography>
            </Stack>
            {filteredJobs.length === 0 ? (
              <Typography color="text.secondary">No roles match your filters. Try adjusting search or filters.</Typography>
            ) : (
              <Grid container spacing={2}>
                {filteredJobs.map((job) => (
                  <Grid item xs={12} sm={6} md={4} key={job.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        ...panelCardSx,
                        '&:hover': {
                          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {job.job_code}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 1, lineHeight: 1.3 }}>
                          {job.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {job.client?.name || job.client?.company_name || '—'} · {job.location || job.city || '—'}
                        </Typography>
                        <Button component={RouterLink} to={`/query/jobs/${job.id}`} variant="contained" size="small" fullWidth>
                          View role
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>

      <Box id="portal-training" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <SchoolIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-training')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: contentPanelTheme.textPrimary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Trainings
          </Typography>
        </Stack>
        {!trainingPublic ? (
          <Typography color="text.secondary">Training catalog is not shown on the public page.</Typography>
        ) : trainingRows.length === 0 ? (
          <Typography color="text.secondary">No published programs to highlight yet.</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Category → Sub category → Course → Version ({versionCountLabel(allTrainingCoursesFlat.length)} total)
              </Typography>
              <TrainingLayoutFormatToggle
                value={trainingLayoutFormat}
                onChange={(v) => {
                  setTrainingLayoutFormat(v);
                  setStoredTrainingLayoutFormat(v);
                }}
              />
            </Stack>
            <TrainingHierarchyLegend />
            <TrainingHierarchyBrowse
              categories={trainingRows.map((cat) => ({
                id: cat.id,
                name: cat.name,
                description: cat.description,
                groups: groupCoursesByLearningSpot(cat.publishedCourses)
              }))}
              layoutFormat={trainingLayoutFormat}
              onOpenVersion={(id) => setTrainingDialogCourseId(id)}
            />
          </Stack>
        )}
      </Box>

      <Box id="portal-exams" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <QuizIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-exams')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: contentPanelTheme.textPrimary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Exams
          </Typography>
        </Stack>
        {!examsPublic ? (
          <Typography color="text.secondary">Exams are not listed publicly for this organization.</Typography>
        ) : exams.length === 0 ? (
          <Typography color="text.secondary">No standalone exams published for public listing.</Typography>
        ) : (
          <Grid container spacing={2}>
            {exams.map((ex) => (
              <Grid item xs={12} sm={6} md={4} key={ex.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', ...panelCardSx }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {ex.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {ex.description
                        ? ex.description.length > 160
                          ? `${ex.description.slice(0, 160)}…`
                          : ex.description
                        : '—'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      {ex.time_limit_minutes != null && <Chip size="small" label={`${ex.time_limit_minutes} min`} />}
                      {ex.pass_score_percent != null && (
                        <Chip size="small" variant="outlined" label={`Pass ${ex.pass_score_percent}%`} />
                      )}
                    </Stack>
                    <Button variant="contained" size="small" fullWidth onClick={() => setExamDialogId(ex.id)}>
                      View details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box id="portal-benches" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <GroupWorkIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-benches')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: contentPanelTheme.textPrimary,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Benches
          </Typography>
        </Stack>
        {!benchesPublic ? (
          <Typography color="text.secondary">Bench marketing is not listed publicly for this organization.</Typography>
        ) : publicBenches.length === 0 ? (
          <Typography color="text.secondary">
            No benches are marked Public yet, or none are operational.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {publicBenches.map((b) => (
              <Grid item xs={12} md={6} key={b.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', ...panelCardSx }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {b.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {b.bench_code || '—'}
                    </Typography>
                    {b.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {b.description.length > 200 ? `${b.description.slice(0, 200)}…` : b.description}
                      </Typography>
                    ) : null}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Candidates ({(b.candidates || []).length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {(b.candidates || []).slice(0, 24).map((c) => (
                        <Chip key={c.id || c.name} size="small" label={c.name || '—'} variant="outlined" />
                      ))}
                      {(b.candidates || []).length > 24 ? (
                        <Chip size="small" label={`+${(b.candidates || []).length - 24} more`} />
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box id="portal-more" sx={{ scrollMarginTop: 88 }}>
        <Card
          sx={{
            borderRadius: 2,
            ...panelCardSx,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
              theme.palette.secondary.main,
              0.05
            )}), ${contentPanelTheme.panelBg}`,
            border: '1px solid',
            borderColor: contentPanelTheme.panelBorder
          }}
        >
          <CardContent sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              More coming
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              Additional public resources will appear here as your organization enables them.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  const splitTrainingCategory =
    trainingBrowseScope === 'category' ? trainingRows.find((c) => c.id === selectedTrainingId) : null;

  const browsePanelScrollable = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 1,
          flexShrink: 0
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
          Browse
        </Typography>
        <Tooltip title="Hide list — wider preview">
          <IconButton size="small" onClick={hideSplitList} aria-label="Hide browse list">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <ToggleButtonGroup
            value={splitTab}
            exclusive
            fullWidth
            orientation={isMdUp ? 'vertical' : 'horizontal'}
            size="small"
            sx={{ mb: 2, alignItems: 'stretch', '& .MuiToggleButton-root': { justifyContent: 'flex-start', py: 1 } }}
            onChange={(e, v) => v != null && onSplitTabChange(v)}
          >
            {jobsPublic && (
              <ToggleButton value="jobs">
                <WorkIcon sx={{ mr: 1, fontSize: 20 }} />
                Jobs ({jobs.length})
              </ToggleButton>
            )}
            {trainingPublic && (
              <ToggleButton value="training">
                <SchoolIcon sx={{ mr: 1, fontSize: 20 }} />
                Trainings ({trainingRows.length})
              </ToggleButton>
            )}
            {examsPublic && (
              <ToggleButton value="exams">
                <QuizIcon sx={{ mr: 1, fontSize: 20 }} />
                Exams ({exams.length})
              </ToggleButton>
            )}
            {benchesPublic && (
              <ToggleButton value="benches">
                <GroupWorkIcon sx={{ mr: 1, fontSize: 20 }} />
                Benches ({publicBenches.length})
              </ToggleButton>
            )}
            <ToggleButton value="more">
              <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20 }} />
              More
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 1 }} />

          {splitTab === 'jobs' && jobsPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setJobSplitView('browse');
                    setDetailJob(null);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Open positions
                </Link>
              </ListSubheader>
              {filteredJobs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  {jobs.length === 0 ? 'No listings.' : 'No listings match filters — adjust filters on the right.'}
                </Typography>
              ) : (
                filteredJobs.map((job) => (
                  <ListItemButton
                    key={job.id}
                    selected={selectedJobId === job.id}
                    onClick={() => openJobDetailInFrame(job.id)}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={job.title}
                      secondary={`${job.job_code} · ${job.location || job.city || '—'}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          )}

          {splitTab === 'training' && trainingPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setTrainingBrowseScope('all');
                    setSelectedTrainingId(null);
                    setTrainingViewMode('category');
                    setSelectedTrainingCourseId(null);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Trainings
                </Link>
              </ListSubheader>
              {trainingRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  None published.
                </Typography>
              ) : (
                <>
                  <ListItemButton
                    selected={trainingBrowseScope === 'all'}
                    onClick={() => {
                      setTrainingBrowseScope('all');
                      setSelectedTrainingId(null);
                      setTrainingViewMode('category');
                      setSelectedTrainingCourseId(null);
                    }}
                  >
                    <ListItemText
                      primary="All trainings"
                      secondary={`${versionCountLabel(allTrainingCoursesFlat.length)} across all categories`}
                    />
                  </ListItemButton>
                  <Divider sx={{ my: 0.5 }} />
                  {trainingRows.map((cat) => (
                    <ListItemButton
                      key={cat.id}
                      selected={trainingBrowseScope === 'category' && selectedTrainingId === cat.id}
                      onClick={() => {
                        setTrainingBrowseScope('category');
                        setSelectedTrainingId(cat.id);
                        setTrainingViewMode('category');
                        setSelectedTrainingCourseId(null);
                      }}
                      sx={{
                        ...trainingLevelSx('category'),
                        mb: 0.5,
                        mx: 0.5,
                        '&.Mui-selected': {
                          bgcolor: TRAINING_HIERARCHY_COLORS.category.bg,
                          borderColor: TRAINING_HIERARCHY_COLORS.category.border,
                          boxShadow: `inset 3px 0 0 ${TRAINING_HIERARCHY_COLORS.category.text}`
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: '#BFDBFE'
                        }
                      }}
                    >
                      <ListItemText
                        primary={cat.name}
                        secondary={versionCountLabel(cat.publishedVersionCount)}
                        primaryTypographyProps={{ fontWeight: 700, color: TRAINING_HIERARCHY_COLORS.category.text }}
                        secondaryTypographyProps={{ color: alpha(TRAINING_HIERARCHY_COLORS.category.text, 0.75) }}
                      />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          )}

          {splitTab === 'exams' && examsPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setExamFilters({ search: '', timeLimit: 'any', passAtLeast: '' });
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Exams
                </Link>
              </ListSubheader>
              {exams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  None listed.
                </Typography>
              ) : filteredExams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  No exams match filters — adjust filters on the right.
                </Typography>
              ) : (
                filteredExams.map((ex) => (
                  <ListItemButton key={ex.id} selected={selectedExamId === ex.id} onClick={() => setSelectedExamId(ex.id)}>
                    <ListItemText primary={ex.name} secondary={ex.description ? ex.description.slice(0, 80) : '—'} />
                  </ListItemButton>
                ))
              )}
            </List>
          )}

      {splitTab === 'benches' && benchesPublic && (
        <List dense disablePadding>
          <ListSubheader component="div" sx={{ lineHeight: 2, fontWeight: 700 }}>
            Public benches
          </ListSubheader>
          {publicBenches.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              No benches are marked Public yet, or none are operational.
            </Typography>
          ) : (
            publicBenches.map((b) => (
              <ListItemButton
                key={b.id}
                selected={selectedBenchId === b.id}
                onClick={() => setSelectedBenchId(b.id)}
              >
                <ListItemText
                  primary={b.name}
                  secondary={`${b.bench_code || '—'} · ${(b.candidates || []).length} candidate(s)`}
                />
              </ListItemButton>
            ))
          )}
        </List>
      )}

      {splitTab === 'more' && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          Future modules will appear in this area.
        </Typography>
      )}
    </>
  );

  const previewPaper = (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        m: { xs: 0, md: 2 },
        mb: { xs: 2, md: 2 },
        p: 3,
        minHeight: { xs: 240, md: 0 },
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        ...splitPreviewPanelCardSx,
        ...splitPreviewPanelTextScopeSx
      }}
    >
      {splitListHidden && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
          <Tooltip title="Show browse list">
            <IconButton size="small" onClick={showSplitList} aria-label="Show browse list">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            Browse list hidden — maximum space for preview
          </Typography>
        </Stack>
      )}
      {splitTab === 'jobs' && !jobsPublic ? (
        <Typography color="text.secondary">Job listings are not available on the public portal.</Typography>
      ) : splitTab === 'jobs' && jobsPublic ? (
        jobSplitView === 'detail' ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setJobSplitView('browse');
                setDetailJob(null);
              }}
              sx={{ mb: 2 }}
            >
              Back to listings
            </Button>
            {detailLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : detailJob ? (
              <PublicJobDetailBody job={detailJob} compact />
            ) : (
              <Alert severity="warning">Could not load this role.</Alert>
            )}
          </Box>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Find a role
            </Typography>
            <TextField
              size="small"
              label="Search"
              placeholder="Title, skills, description, job code…"
              value={jobFilters.search}
              onChange={(e) => setJobFilters((f) => ({ ...f, search: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              size="small"
              label="Location"
              placeholder="City, state, country…"
              value={jobFilters.location}
              onChange={(e) => setJobFilters((f) => ({ ...f, location: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Client / company"
              value={jobFilters.client}
              onChange={(e) => setJobFilters((f) => ({ ...f, client: e.target.value }))}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Work arrangement</InputLabel>
              <Select
                label="Work arrangement"
                value={jobFilters.remote}
                onChange={(e) => setJobFilters((f) => ({ ...f, remote: e.target.value }))}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="remote">Remote</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
                <MenuItem value="onsite">On-site</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Showing {filteredJobs.length} of {jobs.length} roles
            </Typography>
            <List
              dense
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 120,
                maxHeight: { md: 'calc(100dvh - 320px)' },
                border: 1,
                borderColor: splitPreviewPanelTheme.panelBorder,
                borderRadius: 1
              }}
            >
              {filteredJobs.length === 0 ? (
                <ListItemText
                  sx={{ px: 2, py: 1 }}
                  primary={jobs.length === 0 ? 'No public roles yet.' : 'No roles match — try different filters.'}
                />
              ) : (
                filteredJobs.map((job) => (
                  <ListItemButton
                    key={job.id}
                    selected={selectedJobId === job.id}
                    onClick={() => openJobDetailInFrame(job.id)}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={job.title}
                      secondary={`${job.job_code} · ${job.client?.name || job.client?.company_name || '—'} · ${job.location || job.city || '—'}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Tip: click a role for the full description here — no new tab.
            </Typography>
          </Stack>
        )
      ) : splitTab === 'training' && trainingPublic ? (
        trainingViewMode === 'course' && selectedTrainingCourseId ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setTrainingViewMode('category');
                setSelectedTrainingCourseId(null);
              }}
              sx={{ mb: 2 }}
            >
              Back to courses
            </Button>
            {detailTrainingLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : detailTrainingCourse ? (
              <PublicTrainingCourseDetailBlock
                course={detailTrainingCourse}
                summary={trainingFeedbackSummary}
                feedbackEligibility={feedbackEligibility}
                feedbackRating={feedbackRating}
                onFeedbackRatingChange={setFeedbackRating}
                feedbackThumb={feedbackThumb}
                onFeedbackThumbChange={setFeedbackThumb}
                feedbackComment={feedbackComment}
                onFeedbackCommentChange={setFeedbackComment}
                onSubmitFeedback={submitSplitTrainingFeedback}
                feedbackSubmitting={feedbackSubmitting}
                feedbackMessage={feedbackMessage}
                onSelectVersion={(id) => setSelectedTrainingCourseId(id)}
              />
            ) : (
              <Alert severity="warning">Could not load this course.</Alert>
            )}
          </Box>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                {trainingBrowseScope === 'all'
                  ? 'All trainings'
                  : splitTrainingCategory?.name || 'Training program'}
              </Typography>
              <TrainingLayoutFormatToggle
                value={trainingLayoutFormat}
                onChange={(v) => {
                  setTrainingLayoutFormat(v);
                  setStoredTrainingLayoutFormat(v);
                }}
              />
            </Stack>
            <TrainingHierarchyLegend />
            {trainingBrowseScope === 'all' && (
              <>
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Category, sub category, course, version…"
                  value={trainingFilters.search}
                  onChange={(e) => setTrainingFilters((f) => ({ ...f, search: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Delivery</InputLabel>
                    <Select
                      label="Delivery"
                      value={trainingFilters.delivery}
                      onChange={(e) => setTrainingFilters((f) => ({ ...f, delivery: e.target.value }))}
                    >
                      <MenuItem value="any">Any</MenuItem>
                      <MenuItem value="SPT">Self-paced (SPT)</MenuItem>
                      <MenuItem value="ILT">Instructor-led (ILT)</MenuItem>
                    </Select>
                  </FormControl>
                  {learningSpotOptions.length > 0 && (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Learning area</InputLabel>
                      <Select
                        label="Learning area"
                        value={trainingFilters.learningSpotId}
                        onChange={(e) => setTrainingFilters((f) => ({ ...f, learningSpotId: e.target.value }))}
                      >
                        <MenuItem value="">All areas</MenuItem>
                        {learningSpotOptions.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {trainingBrowseScope === 'all'
                ? `Showing ${filteredAllTrainingCourses.length} of ${allTrainingCoursesFlat.length} versions · Category → Sub category → Course → Version`
                : `${versionCountLabel(splitTrainingCategory?.publishedVersionCount)} · click rows to collapse / expand`}
            </Typography>
            <Divider />
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {trainingBrowseScope === 'category' && !splitTrainingCategory ? (
                <Typography color="text.secondary">Select a program on the left, or choose All trainings.</Typography>
              ) : (
                <TrainingHierarchyBrowse
                  categories={filteredTrainingHierarchy}
                  layoutFormat={trainingLayoutFormat}
                  onOpenVersion={(id) => {
                    setSelectedTrainingCourseId(id);
                    setTrainingViewMode('course');
                  }}
                />
              )}
            </Box>
          </Stack>
        )
      ) : splitTab === 'exams' && examsPublic ? (
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary">
            Exams
          </Typography>
          <TextField
            size="small"
            label="Search"
            placeholder="Exam name or description…"
            value={examFilters.search}
            onChange={(e) => setExamFilters((f) => ({ ...f, search: e.target.value }))}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                label="Duration"
                value={examFilters.timeLimit}
                onChange={(e) => setExamFilters((f) => ({ ...f, timeLimit: e.target.value }))}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="short">Up to 45 minutes</MenuItem>
                <MenuItem value="long">Over 45 minutes</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Min pass %"
              type="number"
              placeholder="e.g. 70"
              value={examFilters.passAtLeast}
              onChange={(e) => setExamFilters((f) => ({ ...f, passAtLeast: e.target.value }))}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredExams.length} of {exams.length} exams
          </Typography>
          <Divider />
          {detailExamLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailExam ? (
            <PublicExamDetailBlock exam={detailExam} />
          ) : (
            <Alert severity="warning">Could not load this exam.</Alert>
          )}
        </Stack>
      ) : splitTab === 'benches' && benchesPublic ? (
        (() => {
          const bench = publicBenches.find((b) => b.id === selectedBenchId) || publicBenches[0] || null;
          if (!bench) {
            return (
              <Typography color="text.secondary">
                No benches are marked Public yet, or none are operational.
              </Typography>
            );
          }
          return (
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                Bench preview
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {bench.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bench.bench_code || '—'}
              </Typography>
              {bench.description ? (
                <Typography variant="body2" color="text.secondary">
                  {bench.description}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No description provided for this bench.
                </Typography>
              )}
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Candidates ({(bench.candidates || []).length})
              </Typography>
              {(bench.candidates || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No candidates listed on this public bench yet.
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {(bench.candidates || []).map((c) => (
                    <Chip key={c.id || c.name} size="small" label={c.name || '—'} variant="outlined" />
                  ))}
                </Stack>
              )}
            </Stack>
          );
        })()
      ) : (
        <>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            Preview
          </Typography>
          <SplitMorePreviewPanel splitTab={splitTab} />
        </>
      )}
    </Paper>
  );

  const splitFrameMobile = (
    <Grid
      container
      spacing={0}
      sx={{
        flex: 1,
        minHeight: { xs: 'auto', md: 'calc(100dvh - 64px)' },
        alignItems: 'stretch',
        width: '100%'
      }}
    >
      {!splitListHidden && (
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            ...splitBrowsePanelSurfaceSx
          }}
        >
          <Box
            sx={{
              p: 2,
              flex: 1,
              overflow: 'auto',
              minHeight: { xs: 200, md: 0 },
              ...splitBrowsePanelTextScopeSx
            }}
          >
            {browsePanelScrollable}
          </Box>
        </Grid>
      )}
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: 280, md: 0 } }}>
        {previewPaper}
      </Grid>
    </Grid>
  );

  const splitFrameDesktop = (
    <Box
      ref={splitContainerRef}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        minHeight: { md: 'calc(100dvh - 64px)' },
        width: '100%',
        minWidth: 0
      }}
    >
      {!splitListHidden && (
        <>
          <Box
            sx={{
              flex: `0 0 ${splitLeftPct}%`,
              width: `${splitLeftPct}%`,
              minWidth: 200,
              maxWidth: '48%',
              borderRight: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              ...splitBrowsePanelSurfaceSx
            }}
          >
            <Box sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0, ...splitBrowsePanelTextScopeSx }}>
              {browsePanelScrollable}
            </Box>
          </Box>
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panels"
            onMouseDown={handleSplitDividerPointerDown}
            onTouchStart={handleSplitDividerPointerDown}
            sx={{
              flex: '0 0 10px',
              width: 10,
              ml: '-2px',
              mr: '-2px',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected' }
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 56,
                borderRadius: 1,
                bgcolor: 'divider',
                pointerEvents: 'none',
                boxShadow: (t) => `inset 0 0 0 1px ${t.palette.divider}`
              }}
            />
          </Box>
        </>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{previewPaper}</Box>
    </Box>
  );

  const splitFrame = isMdUp ? splitFrameDesktop : splitFrameMobile;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {layoutMode === 'regular' ? (
        <Container maxWidth="lg" sx={{ py: 2, flex: 1 }}>
          <Box
            sx={{
              mb: 3,
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: contentPanelTheme.panelBorder,
              bgcolor: contentPanelTheme.panelBg,
              color: contentPanelTheme.textPrimary,
              boxShadow: contentPanelTheme.panelShadow
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -56,
                right: -48,
                width: 220,
                height: 220,
                borderRadius: '50%',
                bgcolor: contentPanelTheme.decorColor1,
                pointerEvents: 'none'
              }}
            />
            <Box sx={{ position: 'relative', ...panelTextScopeSx }}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: contentPanelTheme.textSecondary, letterSpacing: 0.14 }}
              >
                {publicBranding.companyName?.trim() || 'ERP for Consulting'}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mt: 0.5,
                  mb: 1,
                  letterSpacing: '-0.02em',
                  color: contentPanelTheme.textPrimary
                }}
              >
              Careers & learning
              </Typography>
              <Typography
                variant="body1"
                sx={{ maxWidth: 640, lineHeight: 1.65, color: contentPanelTheme.textSecondary }}
              >
                {publicBranding.companyTagline?.trim() ||
                  'Browse open roles and published training programs in one bright public portal.'}
              </Typography>
              {anyPublic && (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2.5 }}>
                  {jobsPublic && (
                    <Button component={RouterLink} to={sectionHref('portal-jobs')} variant="contained" size="medium">
                      View jobs
                    </Button>
                  )}
                  {trainingPublic && (
                    <Button
                      component={RouterLink}
                      to={sectionHref('portal-training')}
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderColor: contentPanelTheme.panelBorder,
                        color: contentPanelTheme.textPrimary,
                        bgcolor: contentPanelTheme.inputBg
                      }}
                    >
                      Browse training
                    </Button>
                  )}
                  {benchesPublic && (
                    <Button
                      component={RouterLink}
                      to={sectionHref('portal-benches')}
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderColor: contentPanelTheme.panelBorder,
                        color: contentPanelTheme.textPrimary,
                        bgcolor: contentPanelTheme.inputBg
                      }}
                    >
                      View benches
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
          {alertsBlock}
          {regularSections}
        </Container>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            px: { xs: 1, sm: 2 },
            pt: 1,
            pb: 2
          }}
        >
          <Box sx={{ px: 0.5, pb: 1.5 }}>{alertsBlock}</Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: { xs: 2, md: 3 },
              overflow: 'hidden',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.2),
              boxShadow: `0 24px 56px ${alpha(theme.palette.common.black, 0.12)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.35)
            }}
          >
            <Box
              sx={{
                height: 5,
                flexShrink: 0,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }}
            />
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{splitFrame}</Box>
          </Box>
        </Box>
      )}
      <Dialog
        open={Boolean(trainingDialogCourseId)}
        onClose={() => setTrainingDialogCourseId(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>Course details</DialogTitle>
        <DialogContent dividers>
          {dialogTrainingLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : dialogTrainingCourse ? (
            <PublicTrainingCourseDetailBlock
              course={dialogTrainingCourse}
              summary={dialogTrainingFeedbackSummary}
              feedbackEligibility={dialogFeedbackEligibility}
              feedbackRating={dialogFeedbackRating}
              onFeedbackRatingChange={setDialogFeedbackRating}
              feedbackThumb={dialogFeedbackThumb}
              onFeedbackThumbChange={setDialogFeedbackThumb}
              feedbackComment={dialogFeedbackComment}
              onFeedbackCommentChange={setDialogFeedbackComment}
              onSubmitFeedback={submitDialogTrainingFeedback}
              feedbackSubmitting={dialogFeedbackSubmitting}
              feedbackMessage={dialogFeedbackMessage}
              onSelectVersion={(id) => setTrainingDialogCourseId(id)}
            />
          ) : (
            <Alert severity="warning">Could not load this course.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrainingDialogCourseId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(examDialogId)}
        onClose={() => setExamDialogId(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>Exam details</DialogTitle>
        <DialogContent dividers>
          {dialogExamLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : dialogExam ? (
            <PublicExamDetailBlock exam={dialogExam} />
          ) : (
            <Alert severity="warning">Could not load this exam.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamDialogId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicPortalPage;
